import {
  Component,
  computed,
  ContentChildren, effect, EventEmitter,
  forwardRef,
  Input, OnChanges, Output,
  QueryList,
  signal, SimpleChanges,
  ViewChild,
} from '@angular/core';
import { noop, Subscription } from 'rxjs';
import { OptionComponent } from '../option/option.component';
import { generateId } from '../../../../_core/utils/generate-id';
import { Keys } from '../../../../_core/utils/keys';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { offset, Placement, shift, size } from '@floating-ui/dom';
import { PopoverTriggerDirective } from '../../popover/popover-trigger.directive';
import { TextInputComponent } from '../text-input/text-input.component';
import { selectOffset } from './select-offset';
import { transformOrigin } from '../../../floating-middlewares/transform-origin';
import { FocusableControlValueAccessor } from '../FocusableControlValueAccessor';

type SelectValue<T> = T | T[];

@Component({
  selector: 'opac-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    }
  ]
})
export class SelectComponent<T> implements FocusableControlValueAccessor, OnChanges {
  private optionSelectSubscriptions: Subscription[] = [];
  private initialValue: SelectValue<T> | undefined = undefined;
  private firstTimeProcessingOptions = true;
  private options: OptionComponent<T>[] = [];
  private typingTimeout: ReturnType<typeof setTimeout> | null = null;
  private typingString = '';

  /**
   * Sets autofocus attribute in native input
   */
  @Input() autofocus: boolean = false;
  /**
   * Determines disabled select state
   */
  @Input() set disabled(disabled: boolean) {
    this.isDisabled = disabled;
  }
  /**
   * Determines select id. Defaults to random string.
   */
  @Input() id: string = generateId();
  /**
   * Determines if select is invalid.
   */
  @Input() invalid: boolean = false;
  /**
   * Sets multiple select state. Selected value will be an array of selected options instead of single value.
   */
  @Input() multiple: boolean = false;
  /**
   * Sets required state for select.
   * This prop won't validate, it is just for accessibility reasons.
   */
  @Input() required: boolean = false;

  /**
   * Emits when select value changes.
   */
  @Output() change = new EventEmitter<SelectValue<T>>();

  @ContentChildren(OptionComponent) set contentOptions(options: QueryList<OptionComponent<T>>) {
    this.processOptions(options.toArray()).then((opts) => {
      this.options = opts;
    });
  }

  @ViewChild('textInput') textInput: TextInputComponent;
  @ViewChild('trigger') popoverTrigger: PopoverTriggerDirective;

  // Current keyboard focused option
  focusedOption = signal<OptionComponent<T> | undefined>(undefined);
  // Current selected option
  selectedOptions = signal<OptionComponent<T>[] | undefined>(undefined);
  // Current keyboard focused option index in options array
  focusedOptionIndex = computed(() => this.options.findIndex((option) => option.id === this.focusedOption()?.id) ?? 0);
  // Selected option display text. The text that should be shown to user when select is single option
  displayValue = computed(() => {
    if (!this.multiple) {
      return this.selectedOptions()?.[0].getText();
    }

    return '';
  });
  // Selected options internal value. Real options value, an array when is multiple selection, single value when is single.
  value = computed(() => {
    if (!this.multiple) {
      return this.selectedOptions()?.[0].value;
    }

    return (this.selectedOptions() ?? []).map((option) => option.value);
  });

  // Determines if dropdown is open
  isOpen = signal<boolean>(false);
  isDisabled = false;

  // Control value accessor functions
  onChange: (value: SelectValue<T>) => void = noop;
  onTouch: () => void = noop;

  // Popover options
  popoverId = `${this.id}-listbox`;
  popoverPosition: Placement = 'bottom-start';
  popoverBeforeMiddlewares = [
    offset(4),
    shift({ padding: 16 }),
  ];
  popoverAfterMiddlewares = [
    size({
      apply({ rects, elements }) {
        // Min width must be equal to input width
        Object.assign(elements.floating.style, {
          minWidth: `${rects.reference.width}px`,
        });
      },
    }),
    selectOffset(),
  ];

  constructor() {
    effect(() => {
      const selectedOptions = this.selectedOptions();

      this.options.forEach((option) => {
        option.selected = selectedOptions?.includes(option) ?? false;
      });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.multiple) {
      this.options.forEach(option => {
        option.multiple = this.multiple;
      });
    }
  }

  registerOnChange(fn: (value: SelectValue<T>) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  writeValue(value: SelectValue<T>): void {
    this.initialValue = value;

    const selectedValueOptions = this.options.filter((option) => {
      if (Array.isArray(value)) {
        return value.includes(option.value);
      } else {
        return option.value === value;
      }
    });

    if (this.multiple) {
      this.setSelectedOptions(selectedValueOptions);
    } else if (selectedValueOptions.length > 0) {
      this.setSelectedOptions(selectedValueOptions);
    }
  }

  openDropdown() {
    this.isOpen.set(true);
    this.popoverTrigger.showPopover().then(() => {
      // Prevent focus if it's not visible to cancel scrolling
      setTimeout(() => {
        if (!this.focusedOption()) {
          this.setFocusedOption(this.options[0]);
        }
      }, 1)
    });
  }

  closeDropdown() {
    this.isOpen.set(false);
    this.popoverTrigger.closePopover();
  }

  toggleMenu() {
    if (this.isOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  /**
   * This method runs when popover is dismissed clicking outside (from popover component)
   */
  handlePopoverClose() {
    this.isOpen.set(false);
  }

  /**
   * This method runs once the popover is opened.
   * It manually sets the focus to input in case the popover tries to keep it.
   */
  handlePopoverOpen() {
    this.isOpen.set(true);
    this.textInput.focus();
  }

  handleBlur() {
    this.onTouch();
  }

  /**
   * Input keyboard handle to provide all accessibility shortcuts.
   */
  handleKeyDown(event: KeyboardEvent) {
    const actions = {
      [Keys.ArrowDown]: () => {
        if (altKey) {
          this.handleAltArrowDown();
        } else {
          this.handleArrowDown();
        }
      },
      [Keys.ArrowUp]: () => {
        if (altKey) {
          this.handleAltArrowUp();
        } else {
          this.handleArrowUp();
        }
      },
      [Keys.Enter]: () => this.handleEnter(),
      [Keys.Home]: () => this.handleHome(),
      [Keys.End]: () => this.handleEnd(),
      [Keys.PageUp]: () => this.handlePageUp(),
      [Keys.PageDown]: () => this.handlePageDown(),
      [Keys.Tab]: () => this.handleTab(),
      [Keys.Escape]: () => this.handleEscape(),
    }

    const { altKey, ctrlKey, metaKey, key } = event;

    if (this.multiple && !Object.keys(actions).includes(key)) {
      return;
    }

    if (key === Keys.Space) {
      this.handleSpace();
      return;
    }

    if (!this.multiple && (key === Keys.Backspace || key === Keys.Clear || (key.length === 1 && key !== Keys.Space && !altKey && !ctrlKey && !metaKey))) {
      this.startTyping(key);
      return;
    }

    if (event.key !== Keys.Tab) {
      event.preventDefault();
    }
    actions[event.key]?.();
  }

  handleInput(event: Event) {
    if (this.multiple) {
      const value = (event.target as HTMLInputElement).value;
      this.handleMultipleTyping(value);
    }
  }

  focus() {
    this.textInput.focus();
  }

  private startTyping(key: string) {
    this.openDropdown();

    const searchString = this.getSearchString(key);
    const searchOption = this.getIndexByLetter(searchString, this.focusedOptionIndex() + 1);

    if (searchOption) {
      this.setFocusedOption(searchOption);
    } else {
      if (this.typingTimeout) {
        clearTimeout(this.typingTimeout);
      }

      this.typingString = '';
    }
  }

  private getSearchString(key: string) {
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }

    this.typingTimeout = setTimeout(() => {
      this.typingString = '';
    }, 500);

    this.typingString += key;
    return this.typingString;
  }

  private getIndexByLetter(filter: string, startIndex = 0): OptionComponent<T> | null {
    function filterOptions(options: OptionComponent<T>[], filter: string) {
      return options.filter((option) => option.getText().toLowerCase().indexOf(filter.toLowerCase()) === 0);
    }

    function allSameLetter(array: string[]) {
      return array.every((letter: string) => letter === array[0]);
    }

    const orderedOptions = [
      ...this.options.slice(startIndex),
      ...this.options.slice(0, startIndex),
    ];
    const firstMatch = filterOptions(orderedOptions, filter)[0];

    if (firstMatch) {
      return firstMatch;
    }

    if (allSameLetter(filter.split(''))) {
      const matches = filterOptions(orderedOptions, filter[0]);
      return matches[0];
    }

    return null;
  }

  private handleMultipleTyping(filter: string): void {
    this.options.forEach(option => {
      if (filter === '') {
        option.visible = true;
      } else {
        option.visible = option.getText().toLowerCase().includes(filter.toLowerCase());
      }
    });

    const firstVisibleOption = this.options.find((option) => option.visible);
    if (firstVisibleOption) {
      this.setFocusedOption(firstVisibleOption);
    }
  }

  private handleAltArrowDown() {
    if (!this.isOpen()) {
      this.openDropdown();
    }
  }

  private handleAltArrowUp() {
    if (this.isOpen()) {
      if (!this.multiple) {
        this.addSelectedOption(this.focusedOption()!);
      } else {
        this.toggleSelectedOption(this.focusedOption()!);
      }

      this.closeDropdown();
    }
  }

  private handleArrowUp() {
    if (!this.isOpen()) {
      this.openDropdown();
      this.setFocusedOption(this.options[0]);
      return;
    }

    const previousOption = this.options[this.focusedOptionIndex() - 1] ?? this.options[0];
    this.setFocusedOption(previousOption);
  }

  private handleArrowDown() {
    if (!this.isOpen()) {
      this.openDropdown();
      return;
    }

    const nextOption = this.options[this.focusedOptionIndex() + 1] ?? this.options[this.options.length - 1];
    this.setFocusedOption(nextOption);
  }

  private handleEnter() {
    if (!this.isOpen()) {
      this.openDropdown();
      this.focusedOption()?.scrollIntoView({ block: 'nearest' });
      return;
    }

    if (!this.multiple) {
      this.addSelectedOption(this.focusedOption()!);
      this.closeDropdown();
    } else {
      this.toggleSelectedOption(this.focusedOption()!);
    }
  }

  private handleSpace() {
    this.handleEnter();
  }

  private handleHome() {
    if (!this.isOpen()) {
      this.openDropdown();
    }

    this.setFocusedOption(this.options[0]);
  }

  private handleEnd() {
    if (!this.isOpen()) {
      this.openDropdown();
    }

    this.setFocusedOption(this.options[this.options.length - 1]);
  }

  private handlePageUp() {
    if (this.isOpen()) {
      const previousOption = this.options[this.focusedOptionIndex() - 10] ?? this.options[0];
      this.setFocusedOption(previousOption);
    }
  }

  private handlePageDown() {
    if (this.isOpen()) {
      const nextOption = this.options[this.focusedOptionIndex() + 10] ?? this.options[this.options.length - 1];
      this.setFocusedOption(nextOption);
    }
  }

  private handleTab() {
    if (this.isOpen()) {
      this.onTouch();

      if (!this.multiple) {
        this.addSelectedOption(this.focusedOption()!);
      } else {
        this.toggleSelectedOption(this.focusedOption()!);
      }

      this.closeDropdown();
    }
  }

  private handleEscape() {
    if (this.isOpen()) {
      this.closeDropdown();
    }
  }

  /**
   * Get every `opac-option` component and update it
   */
  private async processOptions(options: OptionComponent<T>[]) {
    // Remove all previous subscriptions to option.select event
    this.optionSelectSubscriptions.forEach((s) => s.unsubscribe());
    this.optionSelectSubscriptions = [];

    // Wait a tick to get `this.initialValue` work
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));

    // Modify every option component
    options.forEach((option, index) => {
      // Update its id based on its index
      option.id = `${this.id}-${index}`;
      option.multiple = this.multiple;

      // Check if this is the first time that options are processed.
      // This only occurs when component is initialized first time, if
      // any option is added/removed via *ngIf or *ngFor this code won't run.
      if (this.firstTimeProcessingOptions) {
        // Check if there is any initial value assigned by the FormControl
        if (this.initialValue) {
          // If initial value is array check if it includes option value
          if (Array.isArray(this.initialValue) && this.initialValue.includes(option.value)) {
            this.addSelectedOption(option);
          // If initial value is not an array just check it with option value
          } else if (this.initialValue === option.value) {
            this.addSelectedOption(option);
          }
        }

        // If there is no initial value set, just select first option if select is not multiple
        if (!this.initialValue && index === 0 && !this.multiple) {
          this.addSelectedOption(option);
        }
      }

      this.optionSelectSubscriptions.push(
        // Once the option is selected set it as focused and selected and close popover
        option.select.subscribe((state) => {
          if (this.multiple) {
            if (state) {
              this.addSelectedOption(option);
            } else {
              this.removeSelectedOption(option);
            }
          } else {
            this.addSelectedOption(option);
          }

          this.setFocusedOption(option);

          if (!this.multiple) {
            this.closeDropdown();
          }
        })
      );
    });

    // Mark component as initialized only if there is any options
    if (options.length > 0) {
      this.firstTimeProcessingOptions = false;
    }
    return options;
  }

  private addSelectedOption(option: OptionComponent<T>, emitChange = true) {
    if (!this.multiple) {
      this.setSelectedOptions([option], emitChange);
      return;
    }

    const selectedOptions = this.selectedOptions() ?? [];
    this.setSelectedOptions([...selectedOptions, option], emitChange);
  }

  private removeSelectedOption(option: OptionComponent<T>, emitChange = true) {
    const selectedOptions = this.selectedOptions() ?? [];
    this.setSelectedOptions(selectedOptions.filter((opt) => opt.id !== option.id), emitChange);
  }

  private toggleSelectedOption(option: OptionComponent<T>, emitChange = true) {
    const selectedOptions = this.selectedOptions() ?? [];
    if (selectedOptions.find((opt) => opt.id === option.id)) {
      this.removeSelectedOption(option, emitChange);
    } else {
      this.addSelectedOption(option, emitChange);
    }
  }

  private setSelectedOptions(options: OptionComponent<T>[], emitChange = true) {
    this.selectedOptions.set(options);

    if (emitChange) {
      this.onChange(this.value());
      this.change.emit(this.value());
    }
  }

  private setFocusedOption(option: OptionComponent<T>) {
    this.options.forEach((o) => o.focused = false);
    option.focused = true;
    option.scrollIntoView({ block: 'nearest' });
    this.focusedOption.set(option);
  }
}
